/**
 * Quotes and sayings (product.quotes-and-sayings): the page's controller.
 *
 * <btf-quotes-preview> sits in the preview block and reads every step through the section:
 * - the style, colour, words, sizes, alignment and caps controls, which are line-item property
 *   radios and fields on Horizon's product form;
 * - the Format radios and the Size variant from btf-product-picker.
 *
 * From those it:
 * - sets the colours and type sizes on the section as custom properties, so every face (the
 *   style cards, the flat preview and the room view) draws the same artwork;
 * - fits the type;
 * - lays out the flat board and the room composite;
 * - fills the order summary and the price.
 *
 * Nothing is drawn on a canvas. The faces are markup from snippets/btf-quotes-face.liquid,
 * styled in assets/btf-quotes.css in cqw units, so one fit factor holds at every size.
 *
 * The data tables are ported from design_handoff_quotes_and_sayings/quote-generator.dc.html:
 * the style factors (LAY), the type sizes, the board sizes in cm (TOWN_CM) and the room
 * rectangles (RM). The colours are in snippets/btf-quotes-colours.liquid.
 */

// Board sizes in cm, as TOWN_CM in the design's site-logic.js
// Older Safari (iOS 15) has no container units: assets/btf-cq.js sets them for the rules that use them
if (!(window.CSS && CSS.supports && CSS.supports('width: 1cqw'))) {
  import((window.btfAssets && window.btfAssets.cq) || './btf-cq.js').then((m) => m.containerUnits('.btf-qart')).catch(() => {});
}

const CM = {
  landscape: { Small: [55, 40], Medium: [75, 55], Large: [100, 75] },
  portrait: { Small: [40, 55], Medium: [55, 75], Large: [75, 100] },
};

// The board's pre-cut corner radius (35 mm, confirmed), which also centres the caps, and the
// cap tile, which carries the real cap and is the same physical size on every board.
const CORNER_CM = 3.5;
const CAP_TILE_CM = 6.6;

// Nothing is drawn within 45 mm of the top or bottom edge, clear of the caps (a 13 mm cap
// centred 35 mm in reaches 41.5 mm). The sides keep each style's own margin.
const CAP_CLEAR_CM = 4.5;

// k scales the saying for each style (the design's LAY table). fonts are the faces each style
// needs loaded before its type can be measured.
const STYLES = {
  letterpress: { k: 1, fonts: ['800 16px "BTFQ Archivo XCond"', '600 16px "BTFQ Archivo"'] },
  classic: { k: 0.9, fonts: ['italic 400 16px "BTFQ Gelasio"', '600 16px "BTFQ Archivo"'] },
  poster: { k: 1.2, kLand: 0.9, fonts: ['900 16px "BTFQ Archivo Expanded"', '700 16px "BTFQ Archivo"'] },
  quotation: {
    k: 0.95,
    fonts: ['400 16px "BTFQ Archivo"', 'italic 500 16px "BTFQ Archivo"', '700 16px "BTFQ Gelasio"'],
  },
  typewriter: { k: 0.82, fonts: ['700 16px "BTFQ Courier Prime"', '400 16px "BTFQ Courier Prime"'] },
  handwritten: { k: 1.2, fonts: ['600 16px "BTFQ Caveat"', '500 16px "BTFQ Caveat"'] },
};

const QUOTE_SIZE = { Smaller: 0.8, Standard: 1, Larger: 1.22 };
const BY_SIZE = { Smaller: 2.6, Standard: 3.2, Larger: 4.4 };

// Every saying starts at its style's full size and shrinks by 8% a step only while it overflows.
// The floor sits below the smallest the old length-based sizing reached (5.2 × 0.45 of a base of
// 10, so 0.234), so the longest sayings still fit.
const FIT_STEP = 0.92;
const FIT_FLOOR = 0.2;
// The attribution keeps its chosen size and has its own factor, used only when it genuinely
// doesn't fit: one of its words is wider than the line, or the saying is already at its floor.
const BY_FIT_FLOOR = 0.5;

// The design's RM table. L and P are the Large board's [left%, top%, width%, height%] in each
// photograph. bias sets how low a smaller board sits in that space (lower on the desk).
// Every room shows in one frame, so the view keeps its size as the customer switches rooms
const ROOM_FRAME = 3 / 2;

const ROOMS = {
  desk: { ar: [1800, 982], bias: 0.8, L: [32.71, 8.12, 38.85, 53.4], P: [35.43, 3.8, 29.07, 71.07] },
  kitchen: { ar: [1600, 1511], bias: 0.5, L: [53.4, 26.2, 22.5, 17.9], P: [56.3, 23.2, 17.2, 24.1] },
  studio: { ar: [1800, 1177], bias: 0.6, L: [38.3, 20.55, 40.8, 46.7], P: [38.1, 5.27, 30.6, 62.4] },
  office: { ar: [1800, 1208], bias: 0.5, L: [50.8, 18.9, 44.3, 49.4], P: [53.25, 13.4, 33.15, 65.8] },
};

// The characters every artwork face can print, since the print file can only set what the fonts
// contain, and the line break. Anything else, such as emoji, is left out as it is typed.
const UNPRINTABLE =
  /[^\n -~ -¬®-ķĹ-ňŊ-žȘ-ț–—‘’‚“”„†-•…‰′″‹›€™−]/gu;

// An attribution that is not printed on the board when a suggestion is used: any proverb
// ("Proverb", "Irish proverb") and our own lines
const UNSIGNED = /proverb|^Beyond the Fridge/i;

// Words a customer types around what they want ("a cat quote", "sayings about dogs"), which
// the search leaves out
const STOP = new Set(
  'a an and about any by for from i in is it me my of on or quote quotes quotation quotations saying sayings some something the to want with words line lines'.split(' ')
);

// Plurals come down to the word: cats → cat, stories → story, boxes → box
const stem = (w) => {
  if (w.length > 4 && w.endsWith('ies')) return w.slice(0, -3) + 'y';
  if (w.length > 4 && /(ch|sh|x|ss)es$/.test(w)) return w.slice(0, -2);
  if (w.length > 3 && w.endsWith('s') && !w.endsWith('ss')) return w.slice(0, -1);
  return w;
};

// A theme's address in a link (?insp=work-and-teamwork)
const slug = (s) => fold(s).replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');

const pct = (n) => n.toFixed(3) + '%';
const fold = (s) =>
  String(s)
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase();

// Typed quotes become typographic ones, as they would be set in print: a quote at the start or
// after a space, bracket or dash opens, any other closes (so "don't" takes an apostrophe). A
// hyphen with a space either side becomes an en dash. Each swap keeps the length, so the caret
// stays put as the customer types.
function curly(text) {
  return text
    // a word that starts with a letter left out takes an apostrophe: 'tis, 'n', 'em, '90s
    .replace(/(^|\s)'(?=(tis|twas|em|cause|til|\d\d)\b|n')/gi, '$1\u2019')
    .replace(/(^|[\s([{\u2013\u2014\u2018\u201c])'/g, '$1\u2018')
    .replace(/'/g, '\u2019')
    .replace(/(^|[\s([{\u2013\u2014\u2018\u201c])"/g, '$1\u201c')
    .replace(/"/g, '\u201d')
    .replace(/ - /g, ' \u2013 ');
}

// Typewriter sets the marks straight, as the machine did
const straight = (text) => text.replace(/[\u2018\u2019]/g, "'").replace(/[\u201c\u201d]/g, '"');

function printable(text) {
  // Line breaks stay, as the customer's own (at most one empty line between two), and so do
  // runs of spaces: the face sets them as typed (white-space: pre-wrap)
  const t = curly(
    String(text || '')
      .replace(/\r\n?/g, '\n')
      .replace(/\t/g, ' ')
      .replace(/\n{3,}/g, '\n\n')
      .replace(/[\u2010\u2011]/g, '-')
      .replace(/[\u2012\u2015]/g, '–')
  );
  const stripped = t.replace(UNPRINTABLE, '');
  // a character taken out from between two words must not leave a double space behind
  const clean = stripped === t ? t : stripped.replace(/ {2,}/g, ' ');
  return { text: clean, dropped: t.length - clean.length };
}

function sizeBand(value) {
  const v = String(value || '');
  if (/small/i.test(v)) return 'Small';
  if (/medium/i.test(v)) return 'Medium';
  if (/large/i.test(v)) return 'Large';
  const nums = (v.match(/\d+(\.\d+)?/g) || []).map(Number);
  const big = Math.max(0, ...nums);
  return big >= 90 ? 'Large' : big >= 65 ? 'Medium' : big > 0 ? 'Small' : 'Large';
}

class QuotesPreview extends HTMLElement {
  #ready = false;
  #fit = 1;
  #byFit = 1;
  #fitRun = 0;
  #capsTouched = false;
  #room = 'desk';
  #roomsInView = false;
  #insp = null;
  #topic = 0;
  #noteTimer = 0;
  #fitting = Promise.resolve();
  #busy = false;

  connectedCallback() {
    if (this.#ready) return;
    this.#ready = true;

    this.section = this.closest('.shopify-section') || document.body;
    this.colours = this.#json('script[data-btf-quotes-colours]') || [];
    this.variants = this.#json('script[data-btf-variants]') || { names: [], list: [] };
    this.rooms = this.#json('script[data-q-rooms]') || {};
    this.quoteInput = this.section.querySelector('[data-q-input="quote"]');
    this.byInput = this.section.querySelector('[data-q-input="by"]');
    this.fitInput = this.querySelector('[data-q-fit]');
    this.flat = this.querySelector('[data-q-flat]');
    this.stage = this.section.querySelector('[data-q-room-stage]');
    this.print = this.#json('script[data-q-print]');

    // The Change link for Step 2 needs somewhere to land in Horizon's picker block
    const picker = this.section.querySelector('.btf-picker-block');
    if (picker && !picker.id) picker.id = 'q-step-format';

    this.#mobilePlaceholder();
    this.#listen();
    this.#watchRooms();
    // Ahead of Horizon's own handler, which listens on the document (assets/component.js)
    window.addEventListener('submit', (e) => this.#submit(e), true);
    if (document.fonts) document.fonts.addEventListener('loadingdone', () => this.#fitType());

    // A fresh page opens with the colour's own caps. Editing a basket line restores its caps, and
    // the name, which carries no form field while it is empty.
    const edit = window.btfEditLine;
    if (edit && edit.active) {
      this.#capsTouched = true;
      Promise.resolve(edit.ready).then(() => {
        const props = (edit.line && edit.line.properties) || {};
        if (this.byInput && props['Who said it'] && !this.byInput.value) this.byInput.value = props['Who said it'];
        this.update();
      });
    } else {
      const params = new URLSearchParams(window.location.search);
      const designKeys = ['style', 'colour', 'orient', 'size', 'caps', 'align', 'qs', 'bs', 'q', 'by'];
      // A shared link opens the design it carries (Stage 2). Without caps in the link, the
      // colour's own caps apply, as on a fresh page.
      if (designKeys.some((key) => params.has(key))) {
        this.setState(QuotesPreview.fromParams(params));
        if (!params.has('caps')) this.#applyColourCaps();
      } else this.#applyColourCaps();
    }
    this.#openInspFromLink();

    // The share strip (assets/btf-share.js) sends this design
    this.share = this.querySelector('btf-share');
    if (this.share) {
      this.share.provider = {
        params: () => QuotesPreview.toParams(this.getState()),
        tokens: () => this.#shareTokens(),
        picture: () => this.#picture(),
        rooms: () => this.#roomPictures(),
        // The fitted type size is part of the picture, though not of the link
        stamp: () => (this.fitInput ? this.fitInput.value : ''),
      };
    }
    this.update();
  }

  #json(selector) {
    const el = this.section.querySelector(selector);
    if (!el) return null;
    try {
      return JSON.parse(el.textContent);
    } catch (e) {
      return null;
    }
  }

  #checked(selector) {
    return this.section.querySelector(selector + ':checked');
  }

  #check(selector, value) {
    const el = Array.from(this.section.querySelectorAll(selector)).find((i) => i.value === value);
    if (el && !el.checked) el.checked = true;
  }

  /* ------------------------------------------------------------------ state */

  /** The design as the form holds it. Stage 2's share link is built from this. */
  getState() {
    const style = this.#checked('[data-q-style]');
    const colour = this.#checked('[data-q-colour]');
    const format = this.#checked('[data-btf-orient]');
    const caps = this.#checked('[data-q-caps]');
    const align = this.#checked('[name="properties[Alignment]"]');
    const qs = this.#checked('[name="properties[Saying size]"]');
    const bs = this.#checked('[name="properties[Who said it size]"]');
    return {
      style: style ? style.dataset.qStyle : 'letterpress',
      styleName: style ? style.value : 'Letterpress',
      colour: colour ? colour.dataset.qColour : 'cream',
      format: format && /portrait/i.test(format.value) ? 'portrait' : 'landscape',
      size: sizeBand(this.#sizeValue()),
      caps: caps && /black/i.test(caps.value) ? 'black' : 'white',
      quote: this.quoteInput ? this.quoteInput.value.trim() : '',
      by: this.byInput ? this.byInput.value.trim() : '',
      quoteSize: qs ? qs.value : 'Standard',
      bySize: bs ? bs.value : 'Standard',
      align: align ? align.value.toLowerCase() : 'left',
    };
  }

  /** Puts a design back into the form, for example from a shared link in Stage 2. */
  setState(s) {
    if (!s) return;
    if (s.style) this.#check('[data-q-style]', (this.section.querySelector(`[data-q-style="${s.style}"]`) || {}).value);
    if (s.colour) this.#check('[data-q-colour]', (this.section.querySelector(`[data-q-colour="${s.colour}"]`) || {}).value);
    if (s.format) this.#check('[data-btf-orient]', s.format === 'portrait' ? 'Portrait' : 'Landscape');
    if (s.caps) {
      this.#check('[data-q-caps]', s.caps === 'black' ? 'Black' : 'White');
      this.#capsTouched = true;
    }
    if (s.quoteSize) this.#check('[name="properties[Saying size]"]', s.quoteSize);
    if (s.bySize) this.#check('[name="properties[Who said it size]"]', s.bySize);
    if (s.align) this.#check('[name="properties[Alignment]"]', s.align.charAt(0).toUpperCase() + s.align.slice(1));
    if (typeof s.quote === 'string' && this.quoteInput) this.quoteInput.value = printable(s.quote).text.trim().slice(0, 120);
    if (typeof s.by === 'string' && this.byInput) this.byInput.value = printable(s.by).text.slice(0, 32);
    if (s.size && s.size !== this.getState().size) {
      // The size is a variant: choosing it through Horizon's picker updates the price and the form
      const radio = Array.from(this.section.querySelectorAll('variant-picker input[type="radio"]')).find(
        (i) => /size/i.test(i.dataset.optionName || '') && sizeBand(i.value) === s.size
      );
      if (radio) radio.click();
    }
    this.update();
  }

  static toParams(s) {
    const p = new URLSearchParams();
    // The format travels as "orient": Shopify reserves ?format= on every storefront page and
    // redirects or 404s any link that carries it.
    ['style', 'colour', 'size', 'caps', 'align'].forEach((k) => s[k] && p.set(k, String(s[k]).toLowerCase()));
    if (s.format) p.set('orient', s.format);
    if (s.quoteSize && s.quoteSize !== 'Standard') p.set('qs', s.quoteSize.toLowerCase());
    if (s.bySize && s.bySize !== 'Standard') p.set('bs', s.bySize.toLowerCase());
    if (s.quote) p.set('q', s.quote);
    if (s.by) p.set('by', s.by);
    return p;
  }

  static fromParams(p) {
    const pick = (v, list) => (list.includes(v) ? v : undefined);
    const cap = (v) => (v ? v.charAt(0).toUpperCase() + v.slice(1) : v);
    const tsizes = ['Smaller', 'Standard', 'Larger'];
    return {
      style: pick(p.get('style'), Object.keys(STYLES)),
      colour: /^[a-z-]{2,20}$/.test(p.get('colour') || '') ? p.get('colour') : undefined,
      format: pick(p.get('orient'), ['landscape', 'portrait']),
      size: pick(cap(p.get('size')), ['Small', 'Medium', 'Large']),
      caps: pick(p.get('caps'), ['white', 'black']),
      align: pick(p.get('align'), ['left', 'centre', 'right']),
      quoteSize: pick(cap(p.get('qs')), tsizes),
      bySize: pick(cap(p.get('bs')), tsizes),
      quote: p.has('q') ? p.get('q') : undefined,
      by: p.has('by') ? p.get('by') : undefined,
    };
  }

  #sizeValue() {
    const picker = this.section.querySelector('variant-picker');
    if (!picker) return '';
    const radio = Array.from(picker.querySelectorAll('input[type="radio"]:checked')).find((i) =>
      /size/i.test(i.dataset.optionName || '')
    );
    if (radio) return radio.value;
    const select = Array.from(picker.querySelectorAll('select')).find((s) => /size/i.test(s.name || ''));
    return select ? select.value : '';
  }

  // The picker opened with no size chosen (blocks/btf-product-picker.liquid) and none is ticked
  // yet: the preview stays on the large board and the price reads "From £75"
  #sizeOpen() {
    const g = this.section.querySelector('.btf-picker__group--size');
    return !!(g && g.querySelector('[data-btf-size-prompt]') && !g.querySelector('input:checked'));
  }

  #variant() {
    const sel = {};
    const picker = this.section.querySelector('variant-picker');
    if (picker) {
      picker.querySelectorAll('input[type="radio"]:checked').forEach((i) => {
        if (i.dataset.optionName) sel[i.dataset.optionName] = i.value;
      });
    }
    const names = this.variants.names || [];
    const list = this.variants.list || [];
    return list.find((v) => names.every((n, i) => sel[n] == null || v.options[i] === sel[n])) || list[0];
  }

  /* ----------------------------------------------------------------- events */

  #listen() {
    this.section.addEventListener('input', (e) => {
      if (e.target === this.quoteInput || e.target === this.byInput) {
        this.#clean(e.target);
        this.update();
      }
    });

    this.section.addEventListener('change', (e) => {
      const t = e.target;
      if (!(t instanceof Element)) return;
      // Only the customer's own choices move the defaults. A basket edit restores every
      // field itself, with untrusted events.
      if (e.isTrusted && t.matches('[data-q-style]')) {
        this.#check('[name="properties[Alignment]"]', { left: 'Left', centre: 'Centre', right: 'Right' }[t.dataset.qAlign]);
      }
      if (e.isTrusted && t.matches('[data-q-colour]') && !this.#capsTouched) this.#applyColourCaps();
      if (e.isTrusted && t.matches('[data-q-caps]')) this.#capsTouched = true;
      this.update();
    });

    this.section.addEventListener('click', (e) => {
      const t = e.target instanceof Element ? e.target : null;
      if (!t) return;
      const room = t.closest('[data-q-room]');
      const change = t.closest('[data-q-change]');
      if (room) {
        this.#room = room.dataset.qRoom;
        this.update();
      } else if (change) {
        e.preventDefault();
        this.#goTo(change.getAttribute('href').slice(1));
      } else if (t.closest('[data-q-insp-toggle]')) {
        this.#toggleInsp();
      } else if (t.closest('[data-q-insp-go]')) {
        this.#search();
      } else if (t.closest('[data-q-topic]')) {
        this.#topic = Number(t.closest('[data-q-topic]').dataset.qTopic);
        const kw = this.section.querySelector('[data-q-insp-kw]');
        if (kw) kw.value = '';
        this.#renderInsp();
      } else if (t.closest('[data-q-use]')) {
        this.#use(t.closest('[data-q-use]'));
      }
    });

    this.section.addEventListener('keydown', (e) => {
      if (e.key === 'Enter' && e.target instanceof Element && e.target.matches('[data-q-insp-kw]')) {
        e.preventDefault();
        this.#search();
      }
    });
  }

  #clean(input) {
    let { text, dropped } = printable(input.value);
    // Nothing but spaces is empty, so the required check stops a board with no words going in
    if (!text.trim()) text = '';
    if (text !== input.value) {
      const at = (input.selectionStart || text.length) - dropped;
      input.value = text;
      input.setSelectionRange(Math.max(0, at), Math.max(0, at));
    }
    if (dropped) {
      const note = this.section.querySelector('[data-q-char-note]');
      if (note) {
        note.hidden = false;
        clearTimeout(this.#noteTimer);
        this.#noteTimer = setTimeout(() => (note.hidden = true), 4000);
      }
    }
  }

  #applyColourCaps() {
    const s = this.getState();
    const c = this.colours.find((x) => x.key === s.colour);
    if (c) this.#check('[data-q-caps]', c.caps === 'black' ? 'Black' : 'White');
  }

  #goTo(id) {
    const target = document.getElementById(id);
    if (!target) return;
    const still = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    target.scrollIntoView({ behavior: still ? 'auto' : 'smooth', block: 'start' });
    const field = target.querySelector('input:checked, textarea, input:not([type="hidden"])');
    if (field) field.focus({ preventScroll: true });
  }

  #mobilePlaceholder() {
    if (!this.byInput || !this.byInput.dataset.placeholderMobile) return;
    const desk = this.byInput.getAttribute('placeholder');
    const mq = window.matchMedia('(max-width: 749px)');
    const set = () => this.byInput.setAttribute('placeholder', mq.matches ? this.byInput.dataset.placeholderMobile : desk);
    mq.addEventListener('change', set);
    set();
  }

  // The room scenes are the heaviest images on the page: they load as Step 5 comes near
  #watchRooms() {
    if (!this.stage) return;
    if (!('IntersectionObserver' in window)) {
      this.#roomsInView = true;
      return;
    }
    const io = new IntersectionObserver(
      (entries) => {
        if (entries.some((en) => en.isIntersecting)) {
          this.#roomsInView = true;
          io.disconnect();
          this.update();
        }
      },
      { rootMargin: '600px 0px' }
    );
    io.observe(this.stage);
  }

  /* ----------------------------------------------------------------- render */

  update() {
    const s = this.getState();
    const root = this.section;
    const colour = this.colours.find((c) => c.key === s.colour) || this.colours[0] || {};
    const cm = CM[s.format][s.size];
    // Anonymous counts (assets/btf-count.js): what the customer moves to, never who they are
    if (window.btfTrack) window.btfTrack(this, 'quotes', { style: s.styleName, colour: colour.name || s.colour, size: s.size, format: s.format === 'portrait' ? 'Portrait' : 'Landscape' });

    root.dataset.qOrient = s.format;
    root.dataset.qCaps = s.caps;
    root.style.setProperty('--q-bg', colour.bg);
    root.style.setProperty('--q-ink', colour.ink);
    root.style.setProperty('--q-accent', colour.accent);
    // The cap clearance as a share of the board's width, since padding percentages are of the width
    root.style.setProperty('--q-safe-y', ((CAP_CLEAR_CM / cm[0]) * 100).toFixed(3) + '%');

    root.querySelectorAll('[data-q-style-name]').forEach((el) => (el.textContent = s.styleName));
    root.querySelectorAll('[data-q-colour-name]').forEach((el) => (el.textContent = colour.name || ''));
    root.querySelectorAll('[data-q-count]').forEach((el) => (el.textContent = s.quote.length + ' / 120'));

    // An empty "Who said it" must not put a blank property on the order
    if (this.byInput) {
      if (s.by) this.byInput.setAttribute('name', 'properties[Who said it]');
      else this.byInput.removeAttribute('name');
    }

    const typed = s.style === 'typewriter' ? straight : (t) => t;
    const words = typed(s.quote || this.dataset.placeholder || '');
    root.querySelectorAll('.btf-qface[data-q-live]').forEach((face) => {
      face.dataset.style = s.style;
      face.dataset.align = s.align;
      const q = face.querySelector('[data-q-quote]');
      const by = face.querySelector('[data-q-by]');
      if (q && q.textContent !== words) q.textContent = words;
      if (by) {
        by.textContent = s.by ? '— ' + typed(s.by) : '';
        by.hidden = !s.by;
      }
    });

    this.#flat(s, cm);
    this.#rooms(s);
    this.#summary(s, colour, cm);
    // The share strip waits until there are words of the customer's own to send
    if (this.share) this.share.hidden = !s.quote;
    this.#price();
    this.#fitType();
  }

  #typeSizes(s) {
    const st = STYLES[s.style];
    // The style's own size, whatever the length: only a real overflow makes it smaller
    const k = s.format === 'landscape' && st.kLand ? st.kLand : st.k;
    const fs = 10 * k * (QUOTE_SIZE[s.quoteSize] || 1) * this.#fit;
    const by = (BY_SIZE[s.bySize] || 3.2) * this.#byFit;
    this.section.style.setProperty('--q-fs', fs.toFixed(2));
    this.section.style.setProperty('--q-byfs', by.toFixed(2));
  }

  /*
   * Resolves once the words are fitted and drawn. A change can start a new fit while an earlier
   * one is awaited, and until the style's fonts load the words sit at full size, so it waits for
   * whichever fit is current, then two frames for the layout to settle. The share picture and the
   * print file both measure the preview, and must never catch it part-way.
   */
  async #settled() {
    let run;
    do {
      run = this.#fitting;
      await run;
      await new Promise((resolve) => requestAnimationFrame(() => requestAnimationFrame(resolve)));
    } while (run !== this.#fitting);
  }

  // The fit loop: once the style's faces have loaded, start at full size and shrink until
  // nothing on the flat preview overflows. The same factor holds on every face, because they
  // are all drawn in cqw.
  #fitType() {
    const run = ++this.#fitRun;
    const s = this.getState();
    this.#fit = 1;
    this.#byFit = 1;
    this.#typeSizes(s);
    const faces = document.fonts ? Promise.all(STYLES[s.style].fonts.map((f) => document.fonts.load(f))) : Promise.resolve();
    this.#fitting = faces
      .catch(() => {})
      .then(() => {
        if (run !== this.#fitRun) return;
        const face = this.flat && this.flat.querySelector('.btf-qface');
        if (!face || face.clientHeight < 20) return;
        // Overflow means anything outside the face's content box, which is inside the padding that
        // keeps the words off the side margins and 45 mm clear of the caps. Spilling into the
        // padding is not overflow to the browser, so each part is checked against that box. A
        // word wider than its own line counts too.
        const over = () => {
          const cs = getComputedStyle(face);
          const f = face.getBoundingClientRect();
          const box = {
            top: f.top + parseFloat(cs.paddingTop) - 1,
            bottom: f.bottom - parseFloat(cs.paddingBottom) + 1,
            left: f.left + parseFloat(cs.paddingLeft) - 1,
            right: f.right - parseFloat(cs.paddingRight) + 1,
          };
          return Array.from(face.children).some((el) => {
            if (el.hidden || getComputedStyle(el).display === 'none') return false;
            const r = el.getBoundingClientRect();
            if (!r.width && !r.height) return false;
            return (
              r.top < box.top || r.bottom > box.bottom || r.left < box.left || r.right > box.right ||
              el.scrollWidth > el.clientWidth + 1
            );
          });
        };
        // Measured with words unbroken, so a word too long for the board shrinks the type rather
        // than splitting. Breaking inside a word is left as the last resort at the floor.
        face.classList.add('is-measuring');
        const by = face.querySelector('[data-q-by]');
        const quote = face.querySelector('[data-q-quote]');
        // The checks above allow a pixel's slack, so a word that only just fits can still be split
        // once words may break again: an element that gains a line without the measuring rule has
        // a split word, and counts as overflowing.
        const splits = (el) => {
          if (!el || el.hidden) return false;
          const h = el.getBoundingClientRect().height;
          face.classList.remove('is-measuring');
          const split = el.getBoundingClientRect().height > h + 0.5;
          face.classList.add('is-measuring');
          return split;
        };
        const shrink = (v, floor) => Math.max(floor, +(v * FIT_STEP).toFixed(3));
        while (over() || splits(quote) || splits(by)) {
          // Only the saying gives way for space. The attribution shrinks only if a word of it is
          // wider than the line, or once the saying can go no smaller.
          const byTooWide = by && !by.hidden && (by.scrollWidth > by.clientWidth + 1 || splits(by));
          if (byTooWide && this.#byFit > BY_FIT_FLOOR) this.#byFit = shrink(this.#byFit, BY_FIT_FLOOR);
          else if (this.#fit > FIT_FLOOR) this.#fit = shrink(this.#fit, FIT_FLOOR);
          else if (this.#byFit > BY_FIT_FLOOR) this.#byFit = shrink(this.#byFit, BY_FIT_FLOOR);
          else break;
          this.#typeSizes(s);
        }
        face.classList.remove('is-measuring');
        if (this.fitInput) this.fitInput.value = String(this.#fit);
        // A picture made before this fit is out of date: make the share picture again
        if (this.share && this.share.prepare) this.share.prepare();
      });
  }

  #flat(s, cm) {
    if (!this.flat) return;
    const tw = (CAP_TILE_CM / cm[0]) * 100;
    const th = (CAP_TILE_CM / cm[1]) * 100;
    const ix = (CORNER_CM / cm[0]) * 100;
    const iy = (CORNER_CM / cm[1]) * 100;
    const f = this.flat.style;
    f.setProperty('--q-ar', cm[0] + ' / ' + cm[1]);
    f.setProperty('--q-rad', ((CORNER_CM * 100) / cm[0]).toFixed(2) + '% / ' + ((CORNER_CM * 100) / cm[1]).toFixed(2) + '%');
    f.setProperty('--q-flat-max', s.format === 'landscape' ? '480px' : '360px');
    f.setProperty('--q-cap-w', pct(tw));
    f.setProperty('--q-cap-h', pct(th));
    f.setProperty('--q-cap-x', pct(ix - tw / 2));
    f.setProperty('--q-cap-y', pct(iy - th / 2));
    const cap = s.caps === 'black' ? this.dataset.capBlack : this.dataset.capWhite;
    if (cap) this.style.setProperty('--q-cap-img', `url("${cap}")`);
  }

  // Step 5, as the design's room compositing:
  // - A Large board is the photograph's own board: the design sits under the in-situ scene,
  //   which is multiplied over it.
  // - Medium and Small boards sit on the empty wall at their true size relative to the Large
  //   board, with its surface mapped onto them on multiply.
  // - A gloss pass puts the reflections back.
  // - The caps are single tiles at a fixed physical size and inset.
  #rooms(s) {
    const stage = this.stage;
    if (!stage) return;
    const rm = ROOMS[this.#room] || ROOMS.desk;
    const LG = s.format === 'landscape' ? rm.L : rm.P;
    const cmR = CM[s.format][s.size];
    const cmL = CM[s.format].Large;
    const w = LG[2] * (cmR[0] / cmL[0]);
    const h = LG[3] * (cmR[1] / cmL[1]);
    const x = LG[0] + (LG[2] - w) / 2;
    const y = LG[1] + (LG[3] - h) * rm.bias;
    const large = s.size === 'Large';
    const st = stage.style;

    stage.dataset.room = this.#room;
    stage.toggleAttribute('data-large', large);
    // The photograph covers the 3:2 frame and is cropped around the Large board's space
    const photo = rm.ar[0] / rm.ar[1];
    const scW = Math.max(1, photo / ROOM_FRAME);
    const scH = Math.max(1, ROOM_FRAME / photo);
    const centre = (pos, len, sc) => Math.min(0, Math.max(1 - sc, 0.5 - ((pos + len / 2) / 100) * sc));
    st.setProperty('--q-sc-w', pct(scW * 100));
    st.setProperty('--q-sc-h', pct(scH * 100));
    st.setProperty('--q-sc-x', pct(centre(LG[0], LG[2], scW) * 100));
    st.setProperty('--q-sc-y', pct(centre(LG[1], LG[3], scH) * 100));
    st.setProperty('--q-bx', pct(x));
    st.setProperty('--q-by', pct(y));
    st.setProperty('--q-bw', pct(w));
    st.setProperty('--q-bh', pct(h));
    st.setProperty('--q-rm-rad', ((CORNER_CM * 100) / cmR[0]).toFixed(2) + '% / ' + ((CORNER_CM * 100) / cmR[1]).toFixed(2) + '%');
    st.setProperty('--q-rm-bgs', (10000 / LG[2]).toFixed(2) + '% ' + (10000 / LG[3]).toFixed(2) + '%');
    st.setProperty('--q-rm-bgp', pct((LG[0] / (100 - LG[2])) * 100) + ' ' + pct((LG[1] / (100 - LG[3])) * 100));

    const urls = this.rooms[this.#room];
    if (this.#roomsInView && urls) {
      st.setProperty('--q-rm-base', `url("${urls.base}")`);
      st.setProperty('--q-rm-img', `url("${s.format === 'landscape' ? urls.land : urls.port}")`);
      st.setProperty('--q-rm-cap', `url("${s.caps === 'black' ? this.rooms.capBlack : this.rooms.capWhite}")`);
    }

    const tile = (CAP_TILE_CM / cmR[0]) * w;
    const tileH = (tile * rm.ar[0]) / rm.ar[1];
    const cxs = [x + (CORNER_CM / cmR[0]) * w, x + w - (CORNER_CM / cmR[0]) * w];
    const cys = [y + (CORNER_CM / cmR[1]) * h, y + h - (CORNER_CM / cmR[1]) * h];
    const corners = [
      [0, 0],
      [1, 0],
      [0, 1],
      [1, 1],
    ];
    stage.querySelectorAll('.btf-qroom__cap').forEach((el) => {
      const [a, b] = corners[Number(el.dataset.i) || 0];
      el.style.left = pct(cxs[a] - tile / 2);
      el.style.top = pct(cys[b] - tileH / 2);
      el.style.width = pct(tile);
      el.style.height = pct(tileH);
    });

    this.section.querySelectorAll('[data-q-room]').forEach((tab) => {
      const on = tab.dataset.qRoom === this.#room;
      tab.classList.toggle('is-on', on);
      tab.setAttribute('aria-pressed', on ? 'true' : 'false');
    });
  }

  #summary(s, colour, cm) {
    const set = (key, text) => this.section.querySelectorAll(`[data-q-sum="${key}"]`).forEach((el) => (el.textContent = text));
    const rows = this.section.querySelector('[data-q-sum]');
    const empty = (rows && rows.closest('[data-empty]') && rows.closest('[data-empty]').dataset.empty) || '';
    const q = s.quote;
    set('style', s.styleName + ' · ' + (colour.name || ''));
    set('words', q ? '“' + (q.length > 48 ? q.slice(0, 46) + '…' : q) + '”' : empty);
    set('format', (s.format === 'landscape' ? 'Landscape' : 'Portrait') + ' · ' + s.size + ', ' + cm[0] + ' × ' + cm[1] + ' cm');
    set('caps', s.caps === 'black' ? 'Black' : 'White');
  }

  #price() {
    const v = this.#variant();
    const open = this.#sizeOpen();
    const price = open ? this.variants.from || '' : v ? v.price_formatted : '';
    this.section.querySelectorAll('[data-btf-price]').forEach((el) => (el.textContent = price));
    this.section
      .querySelectorAll('[data-btf-basket-price]')
      .forEach((el) => (el.textContent = !open && v && v.available ? ' — ' + price : ''));
  }

  /* ---------------------------------------------------------------- sharing */

  /** The board as a picture, for the share strip: drawn the way the print preview is */
  async #picture() {
    if (!this.print || !this.flat) return null;
    const s = this.getState();
    const cm = CM[s.format][s.size];
    const colour = this.colours.find((c) => c.key === s.colour) || {};
    await this.#settled();
    const { buildPicture } = await import(this.print.artwork);
    return buildPicture({
      face: this.flat.querySelector('.btf-qface'),
      board: { w: cm[0] * 10, h: cm[1] * 10 },
      colour,
      fonts: this.print.fonts,
      libs: this.print.libs,
      capUrl: s.caps === 'black' ? this.dataset.capBlack : this.dataset.capWhite,
    });
  }

  /*
   * The design on the four walls of Step 5, for the share strip, drawn with the page's own layers:
   * the empty wall (Medium and Small), the board with its shadow, the in-situ photograph
   * multiplied over it, the gloss pass and the caps. Returns [{ key, label, canvas, focus }].
   */
  async #roomPictures() {
    if (!this.print || !this.flat || !this.rooms || !this.rooms.desk) return [];
    const s = this.getState();
    const cmR = CM[s.format][s.size];
    const cmL = CM[s.format].Large;
    const colour = this.colours.find((c) => c.key === s.colour) || {};
    await this.#settled();
    const [{ buildPicture }, P] = await Promise.all([import(this.print.artwork), import((window.btfAssets && window.btfAssets.picture) || './btf-picture.js')]);
    // The board alone, corners clipped, no caps: the rooms put their own caps on
    const boardImg = await P.loadImage(
      await buildPicture({
        face: this.flat.querySelector('.btf-qface'),
        board: { w: cmR[0] * 10, h: cmR[1] * 10 },
        colour,
        fonts: this.print.fonts,
        libs: this.print.libs,
        capUrl: null,
      })
    );
    const capImg = await P.loadImage(s.caps === 'black' ? this.rooms.capBlack : this.rooms.capWhite).catch(() => null);
    const names = { desk: 'Desk', kitchen: 'Kitchen', studio: 'Studio', office: 'Office' };
    const out = [];
    for (const key of Object.keys(ROOMS)) {
      const rm = ROOMS[key];
      const urls = this.rooms[key];
      if (!urls) continue;
      const LG = s.format === 'landscape' ? rm.L : rm.P;
      const w = LG[2] * (cmR[0] / cmL[0]);
      const h = LG[3] * (cmR[1] / cmL[1]);
      const x = LG[0] + (LG[2] - w) / 2;
      const y = LG[1] + (LG[3] - h) * rm.bias;
      const large = s.size === 'Large';
      const W = 1800;
      const H = (W * rm.ar[1]) / rm.ar[0];
      const c = P.canvas(W, H);
      const ctx = c.getContext('2d');
      const bx = (x / 100) * W;
      const by = (y / 100) * H;
      const bw = (w / 100) * W;
      const bh = (h / 100) * H;
      const rx = (CORNER_CM / cmR[0]) * bw;
      const ry = (CORNER_CM / cmR[1]) * bh;
      const [base, scene] = await Promise.all([large ? null : P.loadImage(urls.base), P.loadImage(s.format === 'landscape' ? urls.land : urls.port)]);

      if (large) {
        ctx.fillStyle = '#eae8e7';
        ctx.fillRect(0, 0, W, H);
      } else {
        ctx.drawImage(base, 0, 0, W, H);
        // The contact shadow the page gives a board on the empty wall
        ctx.save();
        ctx.shadowColor = 'rgba(40, 34, 28, 0.14)';
        ctx.shadowBlur = (18 * W) / 1000;
        ctx.shadowOffsetY = (8 * W) / 1000;
        P.roundedPath(ctx, bx, by, bw, bh, rx, ry);
        ctx.fillStyle = '#ffffff';
        ctx.fill();
        ctx.shadowColor = 'rgba(40, 34, 28, 0.22)';
        ctx.shadowBlur = (2 * W) / 1000;
        ctx.shadowOffsetY = (1 * W) / 1000;
        ctx.fill();
        ctx.restore();
      }
      ctx.save();
      P.roundedPath(ctx, bx, by, bw, bh, rx, ry);
      ctx.clip();
      ctx.drawImage(boardImg, bx, by, bw, bh);
      ctx.restore();

      // The photograph's own board surface, mapped onto this board as the page's background-size
      // and background-position do
      const iw = (bw * 100) / LG[2];
      const ih = (bh * 100) / LG[3];
      const ix = bx + (bw - iw) * (LG[0] / (100 - LG[2]));
      const iy = by + (bh - ih) * (LG[1] / (100 - LG[3]));
      ctx.save();
      ctx.globalCompositeOperation = 'multiply';
      if (large) ctx.drawImage(scene, 0, 0, W, H);
      else {
        P.roundedPath(ctx, bx, by, bw, bh, rx, ry);
        ctx.clip();
        ctx.drawImage(scene, ix, iy, iw, ih);
      }
      ctx.restore();

      // The gloss: the same surface, only its brightest reflections, screened back at 80%
      const g = P.canvas(bw, bh);
      g.getContext('2d').drawImage(scene, ix - bx, iy - by, iw, ih);
      P.highlights(g);
      ctx.save();
      P.roundedPath(ctx, bx, by, bw, bh, rx, ry);
      ctx.clip();
      ctx.globalCompositeOperation = 'screen';
      ctx.globalAlpha = 0.8;
      ctx.drawImage(g, bx, by);
      ctx.restore();

      if (capImg) {
        const tile = (CAP_TILE_CM / cmR[0]) * w;
        const tileH = (tile * rm.ar[0]) / rm.ar[1];
        const cxs = [x + (CORNER_CM / cmR[0]) * w, x + w - (CORNER_CM / cmR[0]) * w];
        const cys = [y + (CORNER_CM / cmR[1]) * h, y + h - (CORNER_CM / cmR[1]) * h];
        cxs.forEach((cx) =>
          cys.forEach((cy) => ctx.drawImage(capImg, ((cx - tile / 2) / 100) * W, ((cy - tileH / 2) / 100) * H, (tile / 100) * W, (tileH / 100) * H))
        );
      }
      out.push({ key, label: names[key] || key, canvas: c, focus: { x: (x + w / 2) / 100, y: (y + h / 2) / 100 } });
    }
    return out;
  }

  // The words for the share messages: {words} {by} {style} {colour} {size} {dims} {price}
  #shareTokens() {
    const s = this.getState();
    const colour = this.colours.find((c) => c.key === s.colour) || {};
    const cm = CM[s.format][s.size];
    const v = this.#variant();
    return {
      words: s.quote || this.dataset.placeholder || '',
      by: s.by ? ' — ' + s.by : '',
      style: s.styleName,
      colour: colour.name || '',
      size: s.size + ' ' + s.format,
      dims: cm[0] + ' × ' + cm[1] + ' cm',
      price: v ? v.price_formatted : '',
    };
  }

  /* ---------------------------------------------------------- add to basket */

  // Add to basket with the print files attached (IMPLEMENTATION-PLAN §5b). The browser makes
  // them from the preview (assets/btf-quotes-artwork.js), then the line posts to /cart/add.js
  // as Shopify file line-item properties. If the files cannot be made or attached, the line
  // still goes in, flagged for the studio to make the artwork from the properties, as Create
  // Your Own does.
  async #submit(e) {
    const form = e.target;
    if (!(form instanceof HTMLFormElement) || !this.section.contains(form) || !form.querySelector('[name="id"]')) return;
    if (!this.print) return; // no print set-up on the page: Horizon adds the line as before
    e.preventDefault();
    e.stopImmediatePropagation();
    if (this.#busy || !form.reportValidity()) return;
    // The customer's go-ahead, ticked beside the button (assets/btf-print-jpeg.js)
    const PJ = await import((window.btfAssets && window.btfAssets.printJpeg) || './btf-print-jpeg.js');
    const approved = PJ.approval(form);
    if (approved === null) return;
    this.#busy = true;

    const button = form.querySelector('[type="submit"]');
    const label = button ? button.innerHTML : '';
    const say = (text) => {
      if (!button) return;
      button.textContent = text;
    };
    if (button) {
      button.disabled = true;
      button.setAttribute('aria-busy', 'true');
    }
    say(this.print.labels.preparing);

    const s = this.getState();
    const cm = CM[s.format][s.size];
    const colour = this.colours.find((c) => c.key === s.colour) || {};
    const stamp = new Date().toISOString().slice(0, 16).replace(/[-:]/g, '').replace('T', '-');
    const base = `quotes-and-sayings-${s.size.toLowerCase()}-${s.format}-${stamp}`;

    let files = null;
    let pending = '';
    try {
      await this.#settled();
      const { buildArtwork } = await import(this.print.artwork);
      files = await buildArtwork({
        face: this.flat.querySelector('.btf-qface'),
        board: { w: cm[0] * 10, h: cm[1] * 10 },
        colour,
        cmyk: this.#json('script[data-btf-quotes-cmyk]'),
        fonts: this.print.fonts,
        libs: this.print.libs,
        capUrl: s.caps === 'black' ? this.dataset.capBlack : this.dataset.capWhite,
        meta: {
          title: `Quotes and sayings · ${s.styleName} · ${colour.name || ''} · ${s.size} ${s.format} ${cm[0] * 10} × ${cm[1] * 10} mm`,
          subject: (s.quote || '') + (s.by ? ' — ' + s.by : ''),
          keywords: [s.styleName, colour.name, s.size, s.format, s.quoteSize, s.bySize, s.align, 'fit ' + (this.fitInput ? this.fitInput.value : '1')],
          layout: '1',
        },
      });
    } catch (err) {
      pending = `yes — the print file was not made in the browser (${err && err.message ? err.message : 'unknown error'}); make the artwork from the properties`;
    }

    let post;
    try {
      post = await import(this.print.cartPost);
    } catch (err) {
      // Without the helper there is no upload: hand the add back to Horizon, files or not
      this.#busy = false;
      if (button) {
        button.disabled = false;
        button.removeAttribute('aria-busy');
        button.innerHTML = label;
      }
      return;
    }

    const lineData = () => {
      const fd = new FormData(form);
      fd.set('quantity', fd.get('quantity') || '1');
      if (approved) fd.append(`properties[${PJ.APPROVED}]`, approved);
      return fd;
    };
    say(this.print.labels.adding);
    let res = null;
    let reason = '';
    // One file: the artwork. Only if it could not be drawn does the PDF go instead, with the
    // small picture the basket shows in place of the product photo (btf-cart-extras.liquid).
    for (const once of [true]) {
      try {
        const fd = lineData();
        if (files) {
          if (files.flat) fd.append(`properties[${PJ.PRINT_FILE}]`, files.flat, base + '-print-150dpi.jpg');
          else {
            fd.append('properties[_Artwork PDF (print from this)]', files.pdf, base + '.pdf');
            fd.append('properties[Your artwork]', files.png, base + '-preview.png');
          }
        } else {
          fd.append('properties[_artwork_pending]', pending);
        }
        res = await post.postForm('/cart/add.js', fd, (p) => say(`${this.print.labels.adding} ${Math.round(p * 100)}%`));
        reason = res ? 'status ' + res.status : 'no response';
      } catch (err) {
        reason = err && err.message ? err.message : 'the upload failed';
      }
      if (post.added(res)) break;
    }
    if (!post.added(res) && files) {
      // Last resort: the line without its files, flagged for the studio
      const fd = lineData();
      fd.append('properties[_artwork_pending]', `yes — the print files did not attach (${reason}); make the artwork from the properties`);
      try {
        res = await post.postForm('/cart/add.js', fd, null);
      } catch (err) {
        res = null;
      }
    }

    if (post.added(res)) {
      await PJ.tidyLine(res.json);
      // Editing a basket line: the original goes only once its replacement is in
      const editing = window.btfEditLine;
      if (editing && editing.active) await editing.replaceOriginal();
      const v = this.#variant();
      await post.announce({ variantId: v && v.id, productId: '', sourceId: 'btf-quotes' });
      window.location.assign('/cart');
      return;
    }

    // Nothing went in: say so on the button and let the customer try again
    this.#busy = false;
    if (button) {
      button.disabled = false;
      button.removeAttribute('aria-busy');
      button.innerHTML = label;
    }
    const msg = (res && res.json && (res.json.description || res.json.message)) || 'Something went wrong adding your board. Please try again.';
    const note = form.closest('.btf-addbasket') || form;
    let err = note.querySelector('[data-q-add-error]');
    if (!err) {
      err = document.createElement('p');
      err.className = 'btf-qfields__note';
      err.dataset.qAddError = '';
      err.setAttribute('role', 'alert');
      note.appendChild(err);
    }
    err.textContent = msg;
  }

  /* ------------------------------------------------------------ inspiration */

  // A link can open the panel on one theme: ?insp=motivational (the For Business pages use it)
  async #openInspFromLink() {
    const want = new URLSearchParams(window.location.search).get('insp');
    if (!want) return;
    await this.#toggleInsp();
    const i = this.#insp ? this.#insp.themes.findIndex((t) => slug(t.name) === slug(want)) : -1;
    if (i >= 0) {
      this.#topic = i;
      this.#renderInsp();
      // On a phone the themes scroll sideways: bring the chosen one into view
      const chip = this.section.querySelector(`[data-q-topic="${i}"]`);
      if (chip) chip.scrollIntoView({ block: 'nearest', inline: 'center' });
    }
    const box = this.section.querySelector('[data-q-insp]');
    if (box) box.scrollIntoView({ block: 'start', behavior: 'auto' });
  }

  async #toggleInsp() {
    const box = this.section.querySelector('[data-q-insp]');
    if (!box) return;
    const btn = box.querySelector('[data-q-insp-toggle]');
    const panel = box.querySelector('.btf-qinsp__panel');
    const open = btn.getAttribute('aria-expanded') !== 'true';
    btn.setAttribute('aria-expanded', open ? 'true' : 'false');
    btn.textContent = open ? box.dataset.closeLabel : box.dataset.openLabel;
    panel.hidden = !open;
    if (open && !this.#insp) {
      try {
        const res = await fetch(box.dataset.src);
        this.#insp = await res.json();
      } catch (e) {
        this.#insp = { themes: [] };
      }
      this.#renderTopics();
      this.#renderInsp();
    }
  }

  #renderTopics() {
    const wrap = this.section.querySelector('[data-q-insp-topics]');
    if (!wrap || !this.#insp) return;
    wrap.replaceChildren(
      ...this.#insp.themes.map((t, i) => {
        const b = document.createElement('button');
        b.type = 'button';
        b.className = 'btf-qinsp__topic';
        b.dataset.qTopic = String(i);
        b.textContent = t.name;
        return b;
      })
    );
  }

  #renderInsp(results, note) {
    const list = this.section.querySelector('[data-q-insp-list]');
    const box = this.section.querySelector('[data-q-insp]');
    if (!list || !this.#insp) return;
    const lead = [];
    if (note) {
      const li = document.createElement('li');
      li.className = 'btf-qinsp__none';
      li.textContent = note;
      lead.push(li);
    }
    this.section.querySelectorAll('[data-q-topic]').forEach((b) => {
      b.setAttribute('aria-pressed', !results && Number(b.dataset.qTopic) === this.#topic ? 'true' : 'false');
    });
    const theme = this.#insp.themes[this.#topic];
    const lines = results || (theme ? theme.lines : []);
    if (!lines.length) {
      const li = document.createElement('li');
      li.className = 'btf-qinsp__none';
      li.textContent = box.dataset.none;
      list.replaceChildren(...(lead.length ? lead : [li]));
      return;
    }
    list.replaceChildren(
      ...lead,
      ...lines.map(([q, by]) => {
        const li = document.createElement('li');
        li.className = 'btf-qinsp__item';
        const qe = document.createElement('span');
        qe.className = 'btf-qinsp__q';
        qe.textContent = q;
        const be = document.createElement('span');
        be.className = 'btf-qinsp__by';
        be.textContent = by;
        const use = document.createElement('button');
        use.type = 'button';
        use.className = 'btf-qinsp__use';
        use.dataset.qUse = '';
        use.dataset.q = q;
        use.dataset.by = by;
        use.textContent = box.dataset.useLabel;
        li.append(qe, be, use);
        return li;
      })
    );
  }

  // A search over the curated lines in every theme, ignoring case, accents and plurals. Every
  // word the customer means must start a word in the line, its credit, its theme or its topic
  // words (a line's third entry: cat → kitten, feline). Short words must match whole, so "tea"
  // does not find "teach". Credits count most, then the line, then themes and topics.
  //
  // A well-known name whose words are still in copyright ("Picasso") finds nothing to print,
  // so the panel says why and offers the lines the library suggests instead.
  #search() {
    const kw = this.section.querySelector('[data-q-insp-kw]');
    if (!this.#insp) return;
    const raw = fold(kw ? kw.value : '');
    const terms = raw
      .split(/[^a-z0-9’']+/)
      .filter((w) => w && !STOP.has(w))
      .map(stem);
    if (!terms.length) {
      this.#renderInsp();
      return;
    }
    const words = (text) => fold(text).split(/[^a-z0-9]+/).filter(Boolean).map(stem);
    const hit = (term, list) => list.some((w) => (term.length <= 3 ? w === term : w.startsWith(term)));
    const seen = new Set();
    const scored = [];
    this.#insp.themes.forEach((t) =>
      t.lines.forEach((line) => {
        if (seen.has(line[0])) return;
        const q = words(line[0]);
        const by = words(line[1]);
        const rest = words(t.name + ' ' + (line[2] || ''));
        let score = 0;
        const all = terms.every((term) => {
          const s = (hit(term, by) ? 3 : 0) + (hit(term, q) ? 2 : 0) + (hit(term, rest) ? 1 : 0);
          score += s;
          return s > 0;
        });
        if (all) {
          seen.add(line[0]);
          scored.push({ line, score });
        }
      })
    );
    const hits = scored.sort((a, b) => b.score - a.score).map((x) => x.line);

    const kept = (this.#insp.protected || []).find((p) => p.names.some((n) => raw.includes(fold(n))));
    if (!hits.length && kept) {
      const box = this.section.querySelector('[data-q-insp]');
      const note = String(box.dataset.protected || '')
        .replace('{who}', kept.who)
        .replace('{died}', kept.died)
        .replace('{free}', kept.free || '');
      const instead = kept.try ? this.#matches(kept.try) : [];
      this.#renderInsp(instead, note);
      return;
    }
    this.#renderInsp(hits);
  }

  // The lines credited to the names the library suggests instead ("Vincent van Gogh")
  #matches(names) {
    const want = [].concat(names).map(fold);
    const out = [];
    this.#insp.themes.forEach((t) =>
      t.lines.forEach((line) => {
        const by = fold(line[1]);
        if (want.some((w) => by.includes(w)) && !out.some((l) => l[0] === line[0])) out.push(line);
      })
    );
    return out;
  }

  #use(btn) {
    if (this.quoteInput) this.quoteInput.value = printable(btn.dataset.q).text.slice(0, 120);
    if (this.byInput) this.byInput.value = UNSIGNED.test(btn.dataset.by) ? '' : printable(btn.dataset.by).text.slice(0, 32);
    this.#toggleInsp();
    this.update();
    if (this.quoteInput) this.quoteInput.focus({ preventScroll: true });
  }
}

if (!customElements.get('btf-quotes-preview')) customElements.define('btf-quotes-preview', QuotesPreview);

export { QuotesPreview };
